import React from 'react';
import {
  Box, Card, CardHeader, CardContent, CardActions, Button, IconButton, Tooltip,
  LinearProgress, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  Autocomplete, TextField, Alert
} from '@mui/material';
import { Refresh, TvOutlined } from '@mui/icons-material';
import BasicTable2 from '../tools/BasicTable2';
import * as ETVConstants from '../ETVConstants';
import FloorDeviceConnection from './FloorDeviceConnection';

class DeviceFloorMgmt extends React.Component {
  state = {
    // locations
    locations: [],
    locationsLoaded: false,
    selected_location_id: '',

    // devices
    devices: [],
    devicesLoaded: false,
    isLoadingDevices: false,

    // ui
    window: '',
    selected_device_id: '',
    error: null,
  };

  componentDidMount() {
    this.loadLocations();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.selected_location_id &&
      prevState.selected_location_id !== this.state.selected_location_id
    ) {
      this.loadDevices();
    }
  }

  // ---------- data ----------
  loadLocations = () => {
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_locations&user_id=${this.props.user_id}`;
    fetch(url)
      .then(res => res.json())
      .then(
        (result) => {
          const list = result.list || [];
          const onlyOne = list.length === 1 ? String(list[0].location_id) : '';
          this.setState({
            locations: list,
            locationsLoaded: true,
            selected_location_id: onlyOne,
            error: null,
          });
        },
        (error) => this.setState({ locations: [], locationsLoaded: false, error })
      );
  };

  loadDevices = () => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/device_mgmt?action=list&owner_id=${this.props.user_id}&location_id=${this.state.selected_location_id}`;

    this.setState({ isLoadingDevices: true, devicesLoaded: false, error: null });

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => {
          const sorted = (result.list || []).slice().sort(ETVConstants.compareDevice);
          this.setState({ devices: sorted, devicesLoaded: true, isLoadingDevices: false });
        },
        (error) => this.setState({ devices: [], devicesLoaded: false, isLoadingDevices: false, error })
      );
  };

  // ---------- ui helpers ----------
  showWindow = (id, show) => this.setState({ window: show ? id : '' });

  setFloorConnection = (deviceID) => {
    this.setState({ selected_device_id: deviceID, window: 'device_floor_div' });
  };

  // ---------- table ----------
  columnsDevices = () => ([
    { title: ETVConstants.trans('name'), field: 'device_name' },
    {
      title: ETVConstants.trans('serving_floors'),
      field: '',
      render: rowData => (
        <Button
          variant="contained"
          size="small"
          startIcon={<TvOutlined />}
          onClick={() => this.setFloorConnection(rowData.id)}
        >
          {ETVConstants.trans('set_floors')}
        </Button>
      ),
    },
  ]);

  // ---------- render ----------
  render() {
    const {
      locations, locationsLoaded, selected_location_id,
      devices, devicesLoaded, isLoadingDevices, error,
    } = this.state;

    const selectedLocation = locations.find(
      l => String(l.location_id) === String(selected_location_id)
    );

    return (
      <Box sx={{ width: 'min(900px, 100%)' }}>
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
			<CardHeader
			title={
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
				<Typography variant="h6">
					{ETVConstants.trans('device_floor_management') || 'Device–Floor Management'}
				</Typography>
				</Box>
			}
			action={
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Autocomplete
					sx={{ width: 280 }}
					options={locations}
					loading={!locationsLoaded}
					value={selectedLocation || null}
					getOptionLabel={(opt) => opt?.name ?? ''}
					isOptionEqualToValue={(opt, val) => String(opt.location_id) === String(val.location_id)}
					onChange={(_, next) => {
					this.setState({
						selected_location_id: next ? String(next.location_id) : '',
						devicesLoaded: false,
						devices: [],
					});
					}}
					renderInput={(params) => (
					<TextField {...params} label={ETVConstants.trans('select_location')} />
					)}
				/>
				</Box>
			}
			sx={(t) => ({
				px: 2,
				borderBottom: `1px solid ${t.palette.divider}`,
				// add space between title and the search field
				'& .MuiCardHeader-action': { ml: { xs: 2, sm: 3 } },
			})}
			/>

          {isLoadingDevices && <LinearProgress />}

          <CardContent sx={{ p: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {ETVConstants.trans('error') || 'Error'}: {String(error)}
              </Alert>
            )}

            {!selected_location_id ? (
              <Alert severity="info">
                {ETVConstants.trans('select_location') || 'Please select a location to see its devices.'}
              </Alert>
            ) : devicesLoaded && devices.length === 0 ? (
              <Alert severity="warning">
                {ETVConstants.trans('no_devices_found') || 'No devices found for this location.'}
              </Alert>
            ) : (
              <BasicTable2
                data={devices}
                columns={this.columnsDevices()}
                title=""
                pageSize={10}
                allow_search
              />
            )}
          </CardContent>

          <CardActions sx={{ px: 2, pb: 2 }}>
            {selected_location_id && (
              <Typography variant="caption" color="text.secondary">
                {ETVConstants.trans('total')} {devices.length || 0}
              </Typography>
            )}
          </CardActions>
        </Card>

        {/* Device ↔ Floors dialog */}
        <Dialog
          open={this.state.window === 'device_floor_div'}
          maxWidth="sm"
          fullWidth
          onClose={() => this.showWindow('', false)}
        >
          <DialogTitle>{ETVConstants.trans('serving_floors')}</DialogTitle>
          <DialogContent dividers>
            {this.state.selected_device_id && selected_location_id ? (
              <FloorDeviceConnection
                device_id={this.state.selected_device_id}
                location_id={selected_location_id}
              />
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.showWindow('', false)}>
              {ETVConstants.trans('close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default DeviceFloorMgmt;
